import { type ReactNode } from 'react';
import {
  Flex,
  Box,
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogBackdrop,
  DialogPositioner,
  Portal,
} from '@chakra-ui/react';
import { LuX, LuTrash2, LuLanguages } from 'react-icons/lu';

interface ModalShellProps {
  open: boolean;
  onClose: () => void;
  title: string;
  maxW?: string;
  children: ReactNode;
  footer: ReactNode;
}

const ModalShell = ({ open, onClose, title, maxW = '480px', children, footer }: ModalShellProps) => (
  <DialogRoot open={open} onOpenChange={(e) => !e.open && onClose()} placement="center">
    <Portal>
      <DialogBackdrop bg="blackAlpha.600" />
      <DialogPositioner
        display="flex"
        justifyContent={{ base: 'center', md: 'flex-end' }}
        alignItems={{ base: 'center', md: 'stretch' }}
      >
        <DialogContent
          backgroundColor="neutral.800"
          borderColor="neutral.700"
          borderWidth="0px"
          borderLeftWidth={{ base: '0px', md: '1px' }}
          borderRadius="0px"
          maxW={{ base: '100%', md: maxW }}
          width="100%"
          height={{ base: '100vh', md: 'auto' }}
          maxH="100vh"
          m="0px"
          p={{ base: '20px', md: '24px' }}
          overflow="auto"
        >
          <DialogHeader pb="0">
            <DialogTitle fontSize="26px" fontWeight="500" color="neutral.100">
              {title}
            </DialogTitle>
            <Box
              as="button"
              position="absolute"
              top="20px"
              right="20px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="32px"
              height="32px"
              borderRadius="6px"
              cursor="pointer"
              color="neutral.400"
              backgroundColor="transparent"
              _hover={{ backgroundColor: 'neutral.700', color: 'neutral.200' }}
              onClick={onClose}
            >
              <LuX size={18} />
            </Box>
          </DialogHeader>
          <DialogBody py="24px">{children}</DialogBody>
          <DialogFooter gap="12px">{footer}</DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </Portal>
  </DialogRoot>
);

export const CancelButton = ({ onClick, label }: { onClick: () => void; label: string }) => (
  <Button
    onClick={onClick}
    height="fit-content"
    py="8px"
    px="16px"
    fontWeight="500"
    fontSize="14px"
    color="neutral.300"
    backgroundColor="transparent"
    borderWidth="1px"
    borderColor="neutral.600"
    _hover={{ borderColor: 'neutral.500', backgroundColor: 'neutral.700' }}
  >
    {label}
  </Button>
);

export const SaveButton = ({ onClick, loading, label, disabled }: { onClick: () => void; loading?: boolean; label: string; disabled?: boolean }) => (
  <Button
    onClick={onClick}
    loading={loading}
    disabled={disabled || loading}
    height="fit-content"
    py="8px"
    px="16px"
    fontWeight="500"
    fontSize="14px"
    color="neutral.900"
    backgroundColor="neutral.100"
    _hover={{ backgroundColor: 'neutral.200' }}
    opacity={disabled ? 0.5 : 1}
  >
    {label}
  </Button>
);

export const DeleteButton = ({ onClick, label }: { onClick: () => void; label: string }) => (
  <Button
    onClick={onClick}
    height="fit-content"
    py="8px"
    px="16px"
    fontWeight="500"
    fontSize="14px"
    color="error.600"
    backgroundColor="transparent"
    borderWidth="1px"
    borderColor="neutral.700"
    _hover={{ borderColor: 'error.600' }}
    mr="auto"
  >
    {label}
  </Button>
);

export const DeleteIconButton = ({ onClick }: { onClick: () => void }) => (
  <Box
    as="button"
    display="flex"
    alignItems="center"
    justifyContent="center"
    width="36px"
    height="36px"
    borderRadius="8px"
    cursor="pointer"
    color="error.600"
    backgroundColor="transparent"
    _hover={{ backgroundColor: '#3B1C1C' }}
    mr="auto"
    onClick={onClick}
  >
    <LuTrash2 size={18} />
  </Box>
);

export const inputStyles = {
  p: '10px',
  fontWeight: '400' as const,
  fontSize: '14px',
  lineHeight: '150%',
  color: 'neutral.300',
  borderWidth: '1px',
  borderColor: 'neutral.700',
  backgroundColor: 'neutral.700',
  borderRadius: '8px',
  _focus: { borderColor: 'neutral.600', outline: 'none' },
};

export const selectStyle: React.CSSProperties = {
  padding: '10px',
  fontWeight: 400,
  fontSize: '14px',
  color: '#CFD5DE',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: '#1A1A1A',
  backgroundColor: '#1A1A1A',
  borderRadius: '8px',
  outline: 'none',
  width: '100%',
};

export const FieldLabel = ({ children }: { children: ReactNode }) => (
  <Box fontWeight="400" fontSize="14px" lineHeight="150%" color="neutral.300">
    {children}
  </Box>
);

export const LanguageTabs = ({
  languages,
  activeLang,
  onSelect,
  options,
  onAutoTranslate,
  isTranslating,
}: {
  languages: string[];
  activeLang: string;
  onSelect: (lang: string) => void;
  options: { value: string; label: string }[];
  onAutoTranslate?: () => void;
  isTranslating?: boolean;
}) => {
  if (languages.length <= 1) return null;
  return (
    <Flex justifyContent="space-between" alignItems="center" gap="8px">
      <Flex
        gap="8px"
        overflow="auto"
        flexShrink={1}
        minWidth={0}
        css={{
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {languages.map((lang) => {
          const opt = options.find(l => l.value === lang);
          return (
            <Box
              key={lang}
              px="10px"
              py="6px"
              fontSize="13px"
              fontWeight="400"
              color={activeLang === lang ? 'neutral.100' : 'neutral.500'}
              backgroundColor={activeLang === lang ? 'neutral.700' : 'transparent'}
              borderWidth="1px"
              borderColor={activeLang === lang ? 'neutral.600' : 'neutral.700'}
              borderRadius="6px"
              cursor="pointer"
              whiteSpace="nowrap"
              flexShrink={0}
              _hover={{ borderColor: 'neutral.500' }}
              onClick={() => onSelect(lang)}
            >
              {opt?.label || lang.toUpperCase()}
            </Box>
          );
        })}
      </Flex>
      {onAutoTranslate && (
        <Box
          as="button"
          display="flex"
          alignItems="center"
          gap="4px"
          px="10px"
          py="6px"
          fontSize="13px"
          fontWeight="400"
          whiteSpace="nowrap"
          flexShrink={0}
          color={isTranslating ? 'neutral.500' : '#7B93DB'}
          backgroundColor="#7B93DB10"
          borderWidth="0px"
          borderRadius="6px"
          cursor={isTranslating ? 'not-allowed' : 'pointer'}
          opacity={isTranslating ? 0.5 : 1}
          _hover={{ backgroundColor: '#7B93DB20', color: '#9BAEE0' }}
          onClick={onAutoTranslate}
          aria-disabled={isTranslating || undefined}
        >
          <LuLanguages size={12} />
          {isTranslating ? 'Translating...' : 'Auto-translate'}
        </Box>
      )}
    </Flex>
  );
};

export default ModalShell;
